import { z } from 'zod';
import {
  EqualisationSection,
  FeesEqualisationTreatment,
} from 'further-types/lp/equalisation';
import { isMongoId } from '../../helpers';
import { CalendarDay } from '../../../utils';
import { AllocationRule } from 'further-types/lp/allocations';
import { AllocationSchema, LinkedActionSchema } from '../reallocation-layer';

const LpFeeSchema = z.object({
  id: z.string().refine(isMongoId()),
  lpFeeTemplateFeeId: z.string().refine(isMongoId()),
  accrualDate: z.instanceof(CalendarDay),
  feeTemplateName: z.string(),
  equalisationFeeExTaxLcy: z.number(),
  equalisationSalesTaxAmountLcy: z.number(),
  equalisationFeeIncTaxLcy: z.number(),
  salesTaxPercentage: z.number(),
  feeExTaxLcy: z.number(),
});

const EqualisationActionSchema = z.object({
  actionId: z.string().refine(isMongoId()),
  actionType: z.string(),
  counterpartyLabel: z.string(),
  ledgerDate: z.instanceof(CalendarDay),
  totalAmountLcy: z.number(),
  allocationRule: z.nativeEnum(AllocationRule),
  originalAllocations: z.array(AllocationSchema).optional(),
  newAllocations: z.array(AllocationSchema),
  linkedActions: z.array(LinkedActionSchema),
});

const EqualisationsTotalCommitmentFeesSchema = z.object({
  commitmentId: z.string().refine(isMongoId()),
  lpFeeTemplateId: z.string().refine(isMongoId()).optional(),
  feesEqualisationTreatment: z.nativeEnum(FeesEqualisationTreatment).optional(),
  investorName: z.string().optional(),
  investorStatus: z.string().optional(),
  feesEqualisationTreatmentPercentage: z.number().optional(),
  totalEqualisationFeeIncTaxLcy: z.number().optional(),
  lpFees: z.array(LpFeeSchema),
  lpFeesByTemplate: z.record(
    z.string(),
    z.object({
      lpFees: z.array(LpFeeSchema),
      feesEqualisationTreatment: z.nativeEnum(FeesEqualisationTreatment),
      feesEqualisationTreatmentPercentage: z.number(),
    }),
  ),
});

export const EqualisationsTotalCommitmentFeesFormSchema = z.object({
  totalCommitmentFees: z.array(EqualisationsTotalCommitmentFeesSchema),
});

const InvestorBreakdownSchema = z.object({
  commitmentId: z.string().refine(isMongoId()),
  investorName: z.string(),
  totalCommitmentLcy: z.number(),
  calledFundsLcy: z.number(),
  allocationPercentage: z.number(),
  excludeFromAllocation: z.boolean(),
});

const NewInvestorBreakdownSchema = z.object({
  commitmentId: z.string().refine(isMongoId()),
  investorName: z.string(),
  totalCommitmentLcy: z.number(),
  calledFundsLcy: z.number(),
  allocationPercentage: z.number(),
  callOverpaidOrUnderpaid: z.number(),
  excludeFromAllocation: z.boolean(),
});

export const GeneralCapitalCallFormSchema = z.object({
  actionId: z.string().refine(isMongoId()),
  entryId: z.string().refine(isMongoId()),
  name: z.string(),
  ledgerDate: z.instanceof(CalendarDay),
  actionDueDate: z.instanceof(CalendarDay).optional(),
  totalAmountLcy: z.number(),
  allocationRule: z.nativeEnum(AllocationRule),
  originalInvestorBreakdown: z.array(InvestorBreakdownSchema),
  newInvestorBreakdown: z.array(NewInvestorBreakdownSchema),
  numberOfEntries: z.number(),
});

export const UpdateEqualisationGeneralCapitalCallsFormSchema = z.object({
  generalCapitalCalls: z.array(GeneralCapitalCallFormSchema),
});

const EqualisationInterestPaymentBreakdownSchema = z.object({
  commitmentId: z.string().refine(isMongoId()),
  investorName: z.string(),
  equalisationInterestTotalLcy: z.number(),
  equalisationInterestPercentage: z.number(),
  aggregateTotalLcy: z.number(),
  excludeFromEqualisationInterest: z.boolean().optional(),
});

export const UpdateEqualisationInterestFormSchema = z.object({
  equalisationInterestPaymentBreakdown: z.array(
    EqualisationInterestPaymentBreakdownSchema,
  ),
});

export const UpdateEqualisationSchema = z.object({
  equalisationId: z.string().refine(isMongoId()),
  vehicleId: z.string().refine(isMongoId()),
  currentSection: z.nativeEnum(EqualisationSection),
  actions: z.array(EqualisationActionSchema).optional(),
  totalCommitmentFees: z
    .array(EqualisationsTotalCommitmentFeesSchema)
    .optional(),
  generalCapitalCalls: z.array(GeneralCapitalCallFormSchema).optional(),
  equalisationInterestPaymentBreakdown: z
    .array(EqualisationInterestPaymentBreakdownSchema)
    .optional(),
  saveActionLinkedCapitalActions: z.boolean().optional(),
  commitmentIds: z.array(z.string().refine(isMongoId())).optional(),
  closeDate: z.instanceof(CalendarDay).optional(),
  name: z.string().optional(),
});

export const UpdateEqualisationActionsFormSchema = z.object({
  currentSection: z.nativeEnum(EqualisationSection),
  actions: z.array(EqualisationActionSchema),
});

export const CommitmentFormSchema = z.object({
  id: z.string().refine(isMongoId()),
  investorName: z.string(),
  isCarryPartnerCommitment: z.boolean().optional(),
  totalCommitmentLcy: z.number(),
  loanCommitmentLcy: z.number(),
  capitalCommitmentLcy: z.number(),
  isCapitalContributionRuleOverride: z.boolean().optional(),
  isSelected: z.boolean(),
});

export const AddCommitmentsToEqualisationFormSchema = z.object({
  closeDate: z.date({
    required_error: 'Close date is required',
  }),
  name: z.string().min(1, 'Close name is required'),
  commitments: z
    .array(CommitmentFormSchema)
    .refine((commitments) => commitments.some((c) => c.isSelected), {
      message: 'Please select at least one commitment',
    }),
});
